
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { HomeHeader } from "../components/home/HomeHeader";
import { AvailabilityType, IMenuItem, OrderType, SubscriptionPlan } from "@tiffin/core";
import { MenuItemModal, MenuView, TifynFooter } from "@tiffin/components";
import { useAppSelector } from "../hooks";
import TifynLogo from "../assets/images/tifyn-logo.png"
import { NavBar } from "../components/NavBar";
import { FloatMenu } from "../components/home/FloatMenu";
import { ViewContext } from "../Contexts";
import { MenuBar } from "../components/MenuBar";
import { OrderDetails } from "../components/cart/OrderDetails";
import { StoreOpenStatus, Utils, getOrderDays } from "@tiffin/app-common";

export const Home = styled(BaseHome)`
  display: grid;
  height: 100%;

  &.showNav {
    grid-template-columns: auto 1fr;
  }

  .page {
    height: calc(var(--vh, 1vh) * 100);
    overflow: auto;
    display: flex;
    flex-direction: column;

    @media (max-width:768px) {
      width: 100vw;
      padding-bottom: 70px;
    }

    .order-details {
      background: #fff;
      padding: 10px 20px 0;
      display: flex;
      align-items: center;
      gap: 5px;
    }

    .menu-container {
      width: 100%;
      padding-bottom: 10px;
      flex-grow: 1;
    }
  }
`

interface IHome {
  className?: string
}

function BaseHome({className}: IHome) {

  const partner = useAppSelector((state) => state.store)
  const cart = useAppSelector((state) => state.cartMulti.carts[partner.details!.id]);

  const [filteredGroups, setFilteredGroups] = useState<Map<string, { order: number; items: IMenuItem[] }>>(new Map())
  const [selectedItem, setSelectedItem] = useState<IMenuItem | null>()
  const [showItemDetailsModal, setShowItemDetailsModal] = useState<boolean>(false)
  const [showCustomAvailabilityMenu, setShowCustomAvailabilityMenu] = useState<boolean>(partner.details?.isRestaurant ? false : true)

  const unfilteredGroups = useRef<Map<string, { order: number; items: IMenuItem[] }>>()
  const pageRef = useRef<HTMLDivElement>(null)
  const view = useContext(ViewContext);

  const dineInClosed = useMemo(() => {
    if(cart && cart.orderType !== undefined && partner.details) {
      const orderDays = getOrderDays(cart.orderType, partner.details.pickupDays, partner.details.deliveryDays)
      const storeState = Utils.getStoreStatus(
        cart.orderType,
        orderDays,
        partner.details.preparationEnabled,
        partner.details.preparationDurationType,
        partner.details.preparationDuration,
        partner.details.adjustedDates,
        partner.details.pauseDates,
        partner.details.preparationInMins
      )
      if(cart.orderType === OrderType.DineIn && (storeState?.status === StoreOpenStatus.Closed || storeState?.status === StoreOpenStatus.NotAcceptingOrders)) {
        return true
      }
    }
  }, [cart, partner.details])

  const staticPlan = partner.details?.subscriptionPlan === SubscriptionPlan.StarterMonthly || partner.details?.subscriptionPlan === SubscriptionPlan.StarterAnnual

  //Parse menu into es6 map
  useEffect(() => {
    if(partner.menu) {
      let groups = new Map()
      let customAvailability = false;

      Object.keys(partner.menu).forEach((key) => {
        groups.set(key, partner.menu![key])
        if(partner.details?.isRestaurant && partner.menu![key].items.find((val) => val.availabilityType === AvailabilityType.Custom)) {
          customAvailability = true;
        }
      })
  
      unfilteredGroups.current = new Map(groups)
      setFilteredGroups(groups)

      if(partner.details?.isRestaurant) {
        setShowCustomAvailabilityMenu(customAvailability)
      }
    }
  }, [partner.menu, partner.details?.isRestaurant])

  function onItemClickCallback(id: string, key: string) {
    if(partner.menu) {
      let item: IMenuItem | undefined;
      for(const [key] of Object.entries(partner.menu)) {
        item = partner.menu![key].items.find((val) => val.id === id);
        if(item) {
          setSelectedItem(item)
          setShowItemDetailsModal(true)
          return;
        }
      }
      setSelectedItem(null)
    }
  }

  return (
    <div className={`${className} ${staticPlan ? 'hideNav' : 'showNav'}`}>
      {
        (!staticPlan && view === "web") && <NavBar />
      }
      <div className="page" ref={pageRef}>
        {
          (partner.details && cart) &&
          <>
            <HomeHeader />
            <div className="order-details">
              <OrderDetails />
            </div>
            <div className="menu-container">
              <MenuView
                partner={partner.details}
                categories={filteredGroups}
                onItemClickCallback={onItemClickCallback}
                orderType={cart.orderType}
                pageRef={pageRef.current}
                displayAvailability={showCustomAvailabilityMenu}
              />
            </div>
            {
              view === "web" &&
              <TifynFooter
                logoAlt="Tifyn Logo"
                logoSrc={TifynLogo}
              />
            }
            <FloatMenu />
            {
              selectedItem && 
              <MenuItemModal 
                open={showItemDetailsModal} 
                onClose={() => setShowItemDetailsModal(false)} 
                menuItem={selectedItem}
                orderType={cart.orderType}
                partner={partner.details}
                modifiers={partner.modifiers}
                isPartnerWebsite={true}
                disableAdd={dineInClosed}
                errorMessage={dineInClosed ? 'We are currently not accepting new orders' : ''}
                staticPlan={staticPlan}
              />
            }
          </>
        }
      </div>
      {
        (!staticPlan && view === "mobile") && <MenuBar />
      }
    </div>
  )
}